import { cn } from "@/lib/utils";
import type { StylePreset } from "@/lib/types";

interface Props {
  styles: StylePreset[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export function StyleCarousel({ styles, selectedId, onSelect }: Props) {
  if (styles.length === 0) {
    return (
      <div className="rounded-md border border-border bg-input px-4 py-3 text-sm text-muted-foreground">
        暂无风格预设。
      </div>
    );
  }
  // Outer label "风格预设" already lives in FreeView (next to the category
  // chip row). We render only the card row here to avoid the duplicate label
  // the user pointed out on 2026-05-20.
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      <StyleCard
        name="不套风格"
        empty
        active={selectedId === null}
        onClick={() => onSelect(null)}
      />
      {styles.map((s) => (
        <StyleCard
          key={s.style_preset_id}
          id={s.style_preset_id}
          name={s.name}
          previewUrl={s.preview_url}
          title={s.prompt_suffix}
          active={selectedId === s.style_preset_id}
          onClick={() => onSelect(s.style_preset_id)}
        />
      ))}
    </div>
  );
}

function StyleCard({
  id,
  name,
  previewUrl,
  title,
  active,
  empty,
  onClick,
}: {
  id?: string;
  name: string;
  previewUrl?: string;
  title?: string;
  active: boolean;
  empty?: boolean;
  onClick: () => void;
}) {
  const fallback = name.slice(0, 2);
  return (
    <button
      type="button"
      title={title}
      data-style-card={id}
      onClick={onClick}
      className={cn(
        "flex w-[100px] flex-shrink-0 flex-col gap-1.5 rounded-md border bg-card p-1.5 transition-all duration-150 hover:-translate-y-0.5 hover:scale-[1.05] hover:border-border/80",
        active && "border-accent shadow-[0_0_0_3px_hsl(var(--accent)/0.2)]"
      )}
    >
      <div
        className={cn(
          "grid aspect-square w-full place-items-center overflow-hidden rounded-sm bg-card text-sm text-muted-foreground",
          empty && "border border-dashed border-border"
        )}
      >
        {previewUrl ? (
          <img src={previewUrl} alt={name} loading="lazy" className="h-full w-full object-cover" />
        ) : empty ? (
          <span>无</span>
        ) : (
          <div className="grid h-full w-full place-items-center bg-gradient-to-br from-accent to-[hsl(8_70%_45%)] text-base font-bold text-accent-foreground">
            {fallback}
          </div>
        )}
      </div>
      <span
        className={cn(
          "text-center text-xs",
          active ? "font-semibold text-accent" : "text-foreground/80"
        )}
      >
        {name}
      </span>
    </button>
  );
}
